/**
 * Componente de Calendário de Prazos - Visual Moderno
 * Visualização mensal dos prazos processuais com destaque visual
 */

import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, AlertTriangle, X, Eye, AlertCircle, CheckCircle } from 'lucide-react';
import { Modal } from './ui';

const CalendarioPrazos = ({ prazos = [], onDateClick, onPrazoClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showPrazosModal, setShowPrazosModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPrazos, setSelectedPrazos] = useState([]);

  // Navegação do calendário
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Calcular dados do mês atual
  const monthData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const currentDay = new Date(startDate);
    
    while (currentDay <= endDate) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return { year, month, firstDay, lastDay, days };
  }, [currentDate]);

  // Agrupar prazos por data
  const prazosPorData = useMemo(() => {
    const grupos = {};
    prazos.forEach(prazo => {
      if (!prazo.dataVencimento) return;
      const data = prazo.dataVencimento.split('T')[0];
      if (!grupos[data]) grupos[data] = [];
      grupos[data].push(prazo);
    });
    return grupos;
  }, [prazos]);

  // Obter prazos de uma data específica
  const getPrazosData = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return prazosPorData[dateString] || [];
  };

  // Estatísticas do mês
  const monthStats = useMemo(() => {
    let vencidos = 0, hoje = 0, semana = 0, total = 0;
    
    monthData.days.forEach(date => {
      if (date.getMonth() !== monthData.month) return;
      const prazosData = getPrazosData(date);
      prazosData.forEach(p => {
        total++;
        if (p.urgencia === 'vencido' || p.diasRestantes < 0) vencidos++;
        else if (p.urgencia === 'hoje' || p.diasRestantes === 0) hoje++;
        else if (p.urgencia === 'esta_semana' || p.diasRestantes <= 7) semana++;
      });
    });
    
    return { vencidos, hoje, semana, total };
  }, [monthData, prazosPorData]);

  const handleDateClick = (date, prazosData) => {
    if (prazosData.length > 0) {
      setSelectedDate(date);
      setSelectedPrazos(prazosData);
      setShowPrazosModal(true);
    }
    if (onDateClick) onDateClick(date, prazosData);
  };

  const isCurrentMonth = (date) => date.getMonth() === monthData.month;
  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  const getDayClass = (date, prazosData) => {
    const hasVencido = prazosData.some(p => p.urgencia === 'vencido' || p.diasRestantes < 0);
    const hasHoje = prazosData.some(p => p.urgencia === 'hoje' || p.diasRestantes === 0);
    const hasSemana = prazosData.some(p => p.urgencia === 'esta_semana');
    
    let base = 'relative min-h-[80px] md:min-h-[100px] p-1 md:p-2 border transition-all duration-200 cursor-pointer group';
    
    if (!isCurrentMonth(date)) {
      base += ' bg-gray-50 text-gray-300 border-gray-100';
    } else if (isWeekend(date)) {
      base += ' bg-slate-50 text-slate-500 border-slate-200';
    } else {
      base += ' bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:shadow-md';
    }
    
    if (isToday(date)) {
      base += ' ring-2 ring-blue-500 ring-offset-1 bg-blue-50';
    }
    
    if (hasVencido) {
      base += ' !bg-gradient-to-br !from-red-100 !to-red-200 !border-red-400';
    } else if (hasHoje) {
      base += ' !bg-gradient-to-br !from-orange-100 !to-red-100 !border-orange-400';
    } else if (hasSemana) {
      base += ' !bg-gradient-to-br !from-amber-50 !to-yellow-100 !border-amber-400';
    } else if (prazosData.length > 0) {
      base += ' !bg-gradient-to-br !from-blue-50 !to-indigo-100 !border-blue-400';
    }
    
    return base;
  };

  const getUrgenciaBadge = (prazo) => {
    if (prazo.urgencia === 'vencido' || prazo.diasRestantes < 0) {
      return { bg: 'bg-red-600', text: '⚠️ VENCIDO', icon: AlertTriangle };
    }
    if (prazo.urgencia === 'hoje' || prazo.diasRestantes === 0) {
      return { bg: 'bg-orange-500', text: '🔥 HOJE', icon: AlertCircle };
    }
    if (prazo.urgencia === 'esta_semana' || prazo.diasRestantes <= 7) {
      return { bg: 'bg-amber-500', text: `⏰ ${prazo.diasRestantes}d`, icon: Clock };
    }
    return { bg: 'bg-blue-500', text: `📅 ${prazo.diasRestantes}d`, icon: Calendar };
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header Moderno */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Título e Navegação */}
          <div className="flex items-center gap-4">
            <div className="p-2 md:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Calendar className="w-6 h-6 md:w-8 md:h-8" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">
                {monthNames[monthData.month]} {monthData.year}
              </h2>
              <p className="text-blue-100 text-sm">Calendário de Prazos</p>
            </div>
          </div>

          {/* Controles */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium text-sm transition-colors"
            >
              Hoje
            </button>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Estatísticas do Mês */}
        <div className="grid grid-cols-4 gap-2 md:gap-4 mt-4 md:mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 md:p-3 text-center">
            <div className="text-xl md:text-2xl font-bold">{monthStats.total}</div>
            <div className="text-xs text-blue-100">Total</div>
          </div>
          <div className="bg-red-500/30 backdrop-blur-sm rounded-xl p-2 md:p-3 text-center">
            <div className="text-xl md:text-2xl font-bold">{monthStats.vencidos}</div>
            <div className="text-xs text-red-100">Vencidos</div>
          </div>
          <div className="bg-orange-500/30 backdrop-blur-sm rounded-xl p-2 md:p-3 text-center">
            <div className="text-xl md:text-2xl font-bold">{monthStats.hoje}</div>
            <div className="text-xs text-orange-100">Hoje</div>
          </div>
          <div className="bg-amber-500/30 backdrop-blur-sm rounded-xl p-2 md:p-3 text-center">
            <div className="text-xl md:text-2xl font-bold">{monthStats.semana}</div>
            <div className="text-xs text-amber-100">Semana</div>
          </div>
        </div>
      </div>

      {/* Cabeçalho dos dias da semana */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {dayNames.map((day, index) => (
          <div 
            key={day} 
            className={`py-2 md:py-3 text-center text-xs md:text-sm font-semibold ${
              index === 0 || index === 6 ? 'text-red-400' : 'text-gray-600'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Grid dos dias */}
      <div className="grid grid-cols-7">
        {monthData.days.map((date, index) => {
          const prazosData = getPrazosData(date);
          
          return (
            <div
              key={index}
              className={getDayClass(date, prazosData)}
              onClick={() => handleDateClick(date, prazosData)}
            >
              {/* Número do dia */}
              <div className={`text-xs md:text-sm font-semibold mb-1 ${
                isToday(date) ? 'text-blue-700' : ''
              }`}>
                {isToday(date) ? (
                  <span className="inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 bg-blue-600 text-white rounded-full">
                    {date.getDate()}
                  </span>
                ) : (
                  date.getDate()
                )}
              </div>

              {/* Prazos do dia */}
              {prazosData.length > 0 && (
                <div className="space-y-1">
                  {prazosData.slice(0, 2).map((prazo, prazoIndex) => {
                    const badge = getUrgenciaBadge(prazo);
                    return (
                      <div
                        key={prazoIndex}
                        className={`${badge.bg} text-white text-[10px] md:text-xs px-1.5 py-0.5 rounded-md truncate font-medium shadow-sm`}
                        title={`${prazo.tipo} - ${prazo.numeroProcesso}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPrazoClick && onPrazoClick(prazo);
                        }}
                      >
                        <span className="hidden md:inline">{prazo.tipo?.substring(0, 10)}</span>
                        <span className="md:hidden">{badge.text}</span>
                      </div>
                    );
                  })}

                  {prazosData.length > 2 && (
                    <div className="text-[10px] md:text-xs text-center font-bold text-gray-600 bg-white/80 rounded px-1">
                      +{prazosData.length - 2} mais
                    </div>
                  )}
                </div>
              )}

              {/* Hover indicator */}
              {prazosData.length > 0 && (
                <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Eye className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs md:text-sm">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-red-100 to-red-200 border border-red-400" />
            <span className="text-gray-600">Vencido</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-orange-100 to-red-100 border border-orange-400" />
            <span className="text-gray-600">Vence Hoje</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-amber-50 to-yellow-100 border border-amber-400" />
            <span className="text-gray-600">Esta Semana</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-400" />
            <span className="text-gray-600">Futuro</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded ring-2 ring-blue-500 bg-blue-50" />
            <span className="text-gray-600">Hoje</span>
          </div>
        </div>
      </div>

      {/* Modal de Prazos do Dia */}
      <Modal
        isOpen={showPrazosModal}
        onClose={() => setShowPrazosModal(false)}
        title={`📅 Prazos de ${selectedDate?.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-500" />
            <span className="font-medium">{selectedPrazos.length} prazo{selectedPrazos.length > 1 ? 's' : ''} para este dia</span>
          </div>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {selectedPrazos.map((prazo, index) => {
              const badge = getUrgenciaBadge(prazo);
              const BadgeIcon = badge.icon;
              
              return (
                <div
                  key={index}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:shadow-lg cursor-pointer transition-all duration-200 bg-white"
                  onClick={() => {
                    onPrazoClick && onPrazoClick(prazo);
                    setShowPrazosModal(false);
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header do prazo */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 ${badge.bg} text-white text-xs font-bold rounded-lg`}>
                          <BadgeIcon className="w-3 h-3" />
                          {badge.text}
                        </span>
                        <span className="text-sm font-bold text-gray-900">
                          {prazo.tipo || prazo.tipoAto || 'Prazo'}
                        </span>
                      </div>

                      {/* Informações do prazo */}
                      <div className="space-y-2 text-sm">
                        {prazo.numeroProcesso && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Processo:</span>
                            <span className="font-mono font-semibold bg-gray-100 px-2 py-0.5 rounded text-gray-800">
                              {prazo.numeroProcesso}
                            </span>
                          </div>
                        )}
                        {prazo.cliente && prazo.cliente !== 'N/A' && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Cliente:</span>
                            <span className="font-semibold text-gray-800">{prazo.cliente}</span>
                          </div>
                        )}
                        {prazo.tribunal && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Tribunal:</span>
                            <span className="font-semibold text-emerald-700">{prazo.tribunal}</span>
                          </div>
                        )}
                        {prazo.descricao && (
                          <div className="p-2 bg-gray-50 rounded-lg text-gray-600 text-xs">
                            {prazo.descricao}
                          </div>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                          <span>⏱️ Prazo: {prazo.dias} dias</span>
                          <span>📆 Restam: {prazo.diasRestantes} dias</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrazoClick && onPrazoClick(prazo);
                        setShowPrazosModal(false);
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 hover:text-white hover:bg-blue-600 border-2 border-blue-200 hover:border-blue-600 rounded-lg transition-all"
                    >
                      <Eye size={16} />
                      <span className="hidden md:inline">Detalhes</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CalendarioPrazos;
